import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';

/**
 * Moves the active CSO to the organization that owns the entity being opened, so the rest of the
 * app agrees with the page being shown.
 *
 * One copy for every entity route that needs it. It used to live only in LocationNavigationGuard,
 * while TankAccessGuard carried a half-written variant keyed on a `src=gts` query parameter - so
 * the switch fired from global search and from nowhere else, and every other link into a tank
 * (the tank grid, location details, tank-move, dashboard widgets, map popups) silently left the
 * user in the wrong organization. Keying the decision on where the user clicked from cannot work:
 * nothing fails loudly when a new link forgets the parameter.
 *
 * The decision belongs to the entity, not to the link. `isInCsoScope` is the server's answer
 * (`IUserSecurity.IsOrgInScope` over OrganizationTree), never an `organizationId === csoId`
 * comparison the client re-derives.
 */
@Injectable({ providedIn: 'root' })
export class CsoFollowService {
  constructor(private authService: AuthService) {}

  /**
   * Resolves `true` in every case - a guard chains this for its side effect, and a failure to
   * switch must never cost the user a page the read gate has already allowed them to see.
   *
   * @param organizationId the entity's owning organization
   * @param isInCsoScope   the server's branch answer for that organization
   */
  followOrganization(
    organizationId: number,
    isInCsoScope: boolean,
  ): Observable<true> {
    if (!organizationId || isInCsoScope) {
      return of(true);
    }

    return this.authService.getUserInformation().pipe(
      take(1),
      switchMap((userInfo) => {
        // Defends against a stale scope answer. TankService caches key tank info in a plain field
        // that no org switch clears, so re-entering the same tank replays `isInCsoScope: false`
        // after the move has already happened - and updateLastSelectedOrganization is not free:
        // it busts the global cache, refetches the profile and resets the tank quick filter.
        if (organizationId === userInfo?.lastSelectedOrganizationId) {
          return of(true as const);
        }

        // Asked before switching rather than after failing. A rejected switch comes back as a 400
        // and updateLastSelectedOrganization raises its own error dialog on the way past, which a
        // guard cannot suppress - so a user who may see the entity but may not become its CSO
        // would meet an error popup before the page loaded. That case is real: a SupplyNet grant
        // on a tank or location carries no org rights.
        return this.authService.canSelectOrganizationForCso(organizationId).pipe(
          take(1),
          switchMap((canSelect) => {
            if (!canSelect) {
              return of(true as const);
            }

            return this.authService
              .updateLastSelectedOrganization(organizationId)
              .pipe(
                take(1),
                map(() => true as const),
                catchError(() => of(true as const)),
              );
          }),
          catchError(() => of(true as const)),
        );
      }),
      catchError(() => of(true as const)),
    );
  }
}
